import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  cancelCommand,
  getDevice,
  getDeviceHistory,
  getTelemetry,
  issueCommand,
  listDevices,
  renameDevice,
  type IssueCommandBody,
} from './api';
import type { Device, FlowTrendRange } from './types';

export const deviceKeys = {
  all: ['devices'] as const,
  list: () => [...deviceKeys.all, 'list'] as const,
  detail: (id: string) => [...deviceKeys.all, 'detail', id] as const,
  telemetry: (id: string, range: FlowTrendRange) =>
    [...deviceKeys.all, 'telemetry', id, range] as const,
  history: (id: string) => [...deviceKeys.all, 'history', id] as const,
};

/** All of the grower's devices (one fetch; page filters/sorts client-side). */
export function useDevices() {
  return useQuery({ queryKey: deviceKeys.list(), queryFn: listDevices });
}

export function useDevice(id: string) {
  return useQuery({ queryKey: deviceKeys.detail(id), queryFn: () => getDevice(id), enabled: !!id });
}

export function useDeviceTelemetry(id: string, range: FlowTrendRange) {
  return useQuery({
    queryKey: deviceKeys.telemetry(id, range),
    queryFn: () => getTelemetry(id, range),
    enabled: !!id,
  });
}

export function useDeviceHistory(id: string) {
  return useQuery({
    queryKey: deviceKeys.history(id),
    queryFn: () => getDeviceHistory(id),
    enabled: !!id,
  });
}

export function useIssueCommand(deviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: IssueCommandBody) => issueCommand(deviceId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: deviceKeys.detail(deviceId) });
    },
  });
}

export function useCancelCommand(deviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (commandId: string) => cancelCommand(deviceId, commandId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: deviceKeys.detail(deviceId) });
    },
  });
}

export function useRenameDevice(deviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => renameDevice(deviceId, name),
    onSuccess: (updated: Device) => {
      qc.setQueryData(deviceKeys.detail(deviceId), updated);
      void qc.invalidateQueries({ queryKey: deviceKeys.list() });
    },
  });
}
